import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Plus, Trash2, Calendar, X, 
  GraduationCap, Users, CheckCircle, XCircle, Eye, 
  Rocket, BookOpen, BrainCircuit
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

const statusColors = {
  in_progress: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', label: 'In Progress' },
  awaiting_evaluation: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', label: 'Awaiting Evaluation' },
  accepted: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', label: 'Accepted' },
  rejected: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', label: 'Rejected' },
};

const AdminInternshipsPage = () => {
  const [activeTab, setActiveTab] = useState('manage'); // manage, submissions, users
  const [internships, setInternships] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [userInternships, setUserInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    field: 'Web Development',
    thumbnail: '',
    length_days: 30,
    steps: []
  });

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (activeTab === 'manage') {
        const res = await axios.get(`${apiUrl}/api/internships`);
        setInternships(res.data || []);
      } else if (activeTab === 'submissions') {
        const res = await axios.get(`${apiUrl}/api/admin/internships/all-submissions`, { headers });
        setSubmissions(res.data || []);
      } else if (activeTab === 'users') {
        const res = await axios.get(`${apiUrl}/api/admin/internships/all-user-internships`, { headers });
        setUserInternships(res.data || []);
      }
    } catch (err) {
      toast.error('Failed to sync internship data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, apiUrl, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateExamAI = async () => {
    if (!formData.title) {
      toast.warning('Please enter a title first to generate a relevant exam');
      return;
    }
    setIsGenerating(true);
    try {
      const res = await axios.post(`${apiUrl}/api/admin/internships/generate-exam`, { topic: formData.title }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const examSteps = res.data.map((q, idx) => ({
        title: `Question ${idx + 1}`,
        description: q.question,
        content: `**Question:** ${q.question}\n\n**Options:**\n${q.options.map((o, i) => `${i + 1}. ${o}`).join('\n')}\n\n**Correct Answer Index:** ${q.answer_index}`,
        step_type: 'learn',
        order: idx + 1
      }));

      // Add project step
      const projectStep = {
        title: 'Final Project',
        description: `Final project for ${formData.title}`,
        content: `Build a comprehensive project implementing the skills learned in this internship.`,
        step_type: 'task',
        order: examSteps.length + 1
      };

      setFormData({
        ...formData,
        description: formData.description || `10-Question Exam and Final Project for ${formData.title}.`,
        steps: [...examSteps, projectStep]
      });
      toast.success('AI Exam Generated Successfully!');
    } catch (err) {
      toast.error('AI Generation Failed');
    } finally {
      setIsGenerating(false);
    }
  };  const generateFullAI = async (prompt) => {
    if (!prompt) return;
    setIsGenerating(true);
    try {
      const res = await axios.post(`${apiUrl}/api/admin/internships/generate-full`, { prompt }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({
        ...formData,
        title: res.data.title,
        description: res.data.description,
        field: res.data.field || 'Web Development',
        steps: res.data.steps.map((s, i) => ({ ...s, order: i + 1 }))
      });
      toast.success('Program Architecture Completed!');
    } catch (err) {
      toast.error('AI Architect failed to build.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateInternship = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${apiUrl}/api/admin/internships`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Add steps
      for (const step of formData.steps) {
        await axios.post(`${apiUrl}/api/admin/internships/${res.data.id}/steps`, step, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success('Internship Program Launched!');
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error('Deployment Failed');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      field: 'Web Development',
      thumbnail: '',
      length_days: 30,
      steps: []
    });
  };

  const handleEvaluate = async (submissionId, status, reason) => {
    try {
      await axios.patch(`${apiUrl}/api/admin/internships/enrollments/${submissionId}/evaluate`, 
        { status, evaluation_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Submission marked as ${status}`);
      setSelectedSubmission(null);
      fetchData();
    } catch (err) {
      toast.error('Evaluation failed');
    }
  };

  const deleteInternship = async (id) => {
    if (!window.confirm('Delete this internship program?')) return;
    try {
      await axios.delete(`${apiUrl}/api/admin/internships/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Program terminated');
      fetchData();
    } catch (err) {
      toast.error('Deletion failed');
    }
  };

  return (
    <div className="space-y-10 animate-fade-in pb-12">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-white/5">
        <div>
          <h1 className="admin-heading text-4xl font-black text-white tracking-tight">Academic HQ</h1>
          <p className="text-slate-500 font-medium mt-1">Configure learning architectures and evaluate talent growth.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => { resetForm(); setShowModal(true); }}
            className="btn-premium btn-premium-primary group"
          >
            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
            <span>Launch Internship</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'manage', label: 'Programs', icon: GraduationCap },
          { id: 'submissions', label: 'Review Submissions', icon: BookOpen },
          { id: 'users', label: 'Active Students', icon: Users }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 rounded-2xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 border ${
              activeTab === tab.id
                ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30 shadow-lg shadow-indigo-500/10'
                : 'bg-white/5 text-slate-500 border-transparent hover:border-white/10 hover:text-white'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="min-h-[400px]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'manage' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                  {internships.map((internship, idx) => (
                    <motion.div 
                      key={internship.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="glass-card overflow-hidden group border-white/5 hover:border-indigo-500/30"
                    >
                      <div className={`h-40 overflow-hidden relative flex items-center justify-center p-6 bg-gradient-to-br ${
                        ['from-indigo-600/20 to-purple-600/20', 'from-blue-600/20 to-cyan-600/20', 'from-emerald-600/20 to-teal-600/20', 'from-rose-600/20 to-orange-600/20'][internship.id % 4]
                      } border-b border-white/5`}>
                         {/* Abstract Pattern Overlay */}
                         <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '16px 16px' }} />
                         
                         {/* Centered Decorative Title */}
                         <div className="relative z-10 text-center">
                           <h4 className="text-xl font-black text-white/40 uppercase tracking-[0.2em] leading-tight select-none pointer-events-none">
                             {internship.title.split(' ').map(w => w[0]).join('')}
                           </h4>
                           <div className="mt-2 h-[2px] w-8 bg-indigo-500/50 mx-auto" />
                         </div>

                         <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] to-transparent opacity-60" />
                         <div className="absolute top-4 right-4 flex gap-2">
                           <button 
                             onClick={() => deleteInternship(internship.id)}
                             className="p-2 rounded-lg bg-red-500/20 text-red-400 opacity-0 group-hover:opacity-100 transition-all border border-red-500/30"
                           >
                             <Trash2 size={16} />
                           </button>
                         </div>
                         <div className="absolute bottom-4 left-4">
                           <span className="px-2 py-1 rounded bg-indigo-500 text-white text-[10px] font-black uppercase tracking-widest">{internship.field}</span>
                         </div>
                      </div>
                      <div className="p-6">
                         <h3 className="text-xl font-bold text-white mb-2 line-clamp-1">{internship.title}</h3>
                         <p className="text-sm text-slate-400 line-clamp-3 mb-6 font-medium leading-relaxed italic border-l-2 border-indigo-500/30 pl-4">
                           "{internship.description}"
                         </p>
                         <div className="flex items-center justify-between text-[10px] font-bold text-slate-500 uppercase tracking-widest pt-4 border-t border-white/5">
                           <span className="flex items-center gap-1"><Calendar size={12} /> {internship.length_days} Days</span>
                           <span className="flex items-center gap-1"><BookOpen size={12} /> {internship.field}</span>
                         </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {internships.length === 0 && (
                  <div className="col-span-full py-20 text-center glass-card border-dashed">
                    <GraduationCap size={48} className="mx-auto mb-4 opacity-20" />
                    <p className="text-slate-500 font-bold tracking-tight">No active learning architectures.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'submissions' && (
              <div className="space-y-4">
                {submissions.map((sub, idx) => (
                  <motion.div 
                    key={sub.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => setSelectedSubmission(sub)}
                    className="glass-card p-6 cursor-pointer hover:border-indigo-500/30 transition-all group border-white/5"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                         <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center text-indigo-400 border border-white/5 group-hover:scale-110 transition-transform">
                           <Rocket size={20} />
                         </div>
                         <div>
                           <h3 className="text-lg font-bold text-white uppercase tracking-tight">{sub.UserInternship?.User?.full_name || 'Anonymous Student'}</h3>
                           <p className="text-xs text-indigo-400 font-bold">{sub.UserInternship?.Internship?.title}</p>
                         </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${statusColors[sub.UserInternship?.status]?.bg} ${statusColors[sub.UserInternship?.status]?.text} ${statusColors[sub.UserInternship?.status]?.border}`}>
                          {statusColors[sub.UserInternship?.status]?.label}
                        </span>
                        <button className="btn-premium btn-premium-outline p-2 border-none">
                          <Eye size={20} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {submissions.length === 0 && (
                  <div className="py-20 text-center glass-card border-dashed">
                    <BookOpen size={48} className="mx-auto mb-4 opacity-20" />
                    <p className="text-slate-500 font-bold tracking-tight">No submissions awaiting evaluation.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="glass-card border-white/5 overflow-hidden">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Email</th>
                      <th>Internship</th>
                      <th>Status</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userInternships.map((enroll) => (
                      <tr key={enroll.id}>
                        <td>
                          <div className="flex items-center gap-3">
                             <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-xs font-bold ring-1 ring-indigo-500/30">
                               {enroll.User?.full_name?.[0] || 'U'}
                             </div>
                             <span className="font-bold text-sm">{enroll.User?.full_name}</span>
                          </div>
                        </td>
                        <td className="text-sm text-slate-500">{enroll.User?.email}</td>
                        <td className="text-sm font-bold text-white">{enroll.Internship?.title}</td>
                        <td>
                          <span className={`px-2 py-1 rounded-md text-[9px] font-black uppercase tracking-widest ${statusColors[enroll.status]?.bg} ${statusColors[enroll.status]?.text}`}>
                            {enroll.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="text-[10px] font-bold text-slate-600">{new Date(enroll.enrollment_date).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Creation Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)} className="absolute inset-0 bg-black/80 backdrop-blur-md" />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-panel w-full max-w-4xl max-h-[90vh] overflow-y-auto z-10 p-0 border-white/10 shadow-2xl">
              <div className="p-8 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0f172a]/80 backdrop-blur-md z-10">
                <div>
                  <h2 className="admin-heading text-2xl font-black text-white uppercase tracking-tight">Configuration Matrix</h2>
                  <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-1">Laying the foundation for academic expansion</p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/5 rounded-full transition-colors text-slate-500 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <div className="p-8 pb-0 space-y-4">
                <div className="glass-card p-6 border-indigo-500/20 bg-indigo-500/5">
                  <label className="block text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-3">AI Rapid Architecture</label>
                  <div className="flex gap-3">
                    <input 
                      type="text" 
                      className="input-premium flex-1 bg-white/5 border-white/10" 
                      placeholder="e.g., A 30-day intensive React and Node.js bootcamp with a final e-commerce project"
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                    />
                    <button 
                      type="button"
                      onClick={() => generateFullAI(aiPrompt)}
                      disabled={isGenerating || !aiPrompt}
                      className="btn-premium btn-premium-primary px-8"
                    >
                      {isGenerating ? 'Building...' : 'Build Program'}
                    </button>
                  </div>
                </div>
              </div>

              <form onSubmit={handleCreateInternship} className="p-8 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Architectural Title</label>
                      <input type="text" className="input-premium font-bold tracking-tight" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., Full Stack Systems Engineering" required />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Program Field</label>
                      <select className="input-premium font-bold" value={formData.field} onChange={(e) => setFormData({...formData, field: e.target.value})}>
                        <option value="Web Development">Web Development</option>
                        <option value="Mobile App Development">Mobile App Development</option>
                        <option value="Game Development">Game Development</option>
                        <option value="Data Science / AI">Data Science / AI</option>
                        <option value="Cloud & DevOps">Cloud & DevOps</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Deployment Duration (Days)</label>
                      <input type="number" className="input-premium font-bold" value={formData.length_days} onChange={(e) => setFormData({...formData, length_days: parseInt(e.target.value)})} required />
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Core Objectives (Description)</label>
                      <textarea className="input-premium h-32 resize-none py-3" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Specify the operational goals..." required />
                    </div>
                    <div>
                       <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Visual Cover URL</label>
                       <input type="text" className="input-premium font-bold" value={formData.thumbnail} onChange={(e) => setFormData({...formData, thumbnail: e.target.value})} placeholder="HTTPS Link to Thumbnail" />
                    </div>
                    <button 
                      type="button" 
                      onClick={generateExamAI}
                      disabled={isGenerating}
                      className="btn-premium btn-premium-outline w-full py-4 border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10"
                    >
                      {isGenerating ? (
                        <>
                          <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin shrink-0" />
                          <span>Executing AI Logic...</span>
                        </>
                      ) : (
                        <>
                          <BrainCircuit size={20} className="animate-pulse" />
                          <span>Generate 10-Question Exam (AI)</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Steps Preview */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest">Procedural Architecture ({formData.steps.length} Nodes)</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
                    {formData.steps.map((step, i) => (
                      <div key={i} className="p-4 bg-white/5 border border-white/5 rounded-xl flex items-center gap-3 group">
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-500 text-xs font-bold ring-1 ring-indigo-500/30">
                          {i + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-white truncate">{step.title}</p>
                          <p className="text-[10px] text-slate-500 font-medium">{step.step_type.toUpperCase()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4 pt-8 border-t border-white/5">
                  <button type="submit" className="btn-premium btn-premium-primary flex-[2] py-4 text-base shadow-indigo-500/30">
                    Establish Program
                  </button>
                  <button type="button" onClick={() => setShowModal(false)} className="btn-premium btn-premium-outline flex-1 border-white/10 uppercase font-black text-xs tracking-widest">
                    Abort
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Submission Detail Drawer */}
      <AnimatePresence>
        {selectedSubmission && (
          <div className="fixed inset-0 z-[100] flex justify-end">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedSubmission(null)} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 30, stiffness: 300 }} className="relative w-full max-w-lg glass-panel h-full overflow-y-auto border-l border-white/10 p-8">
              <div className="flex items-center justify-between mb-10">
                <h2 className="admin-heading text-xl font-black text-white uppercase tracking-tight">Evaluate Objective</h2>
                <button onClick={() => setSelectedSubmission(null)} className="p-2 hover:bg-white/5 rounded-xl text-slate-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-8">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 flex items-center justify-center text-indigo-400 border border-indigo-500/30 shadow-lg">
                    <Users size={28} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white tracking-tight">{selectedSubmission.UserInternship?.User?.full_name}</h3>
                    <p className="text-sm text-indigo-400 font-black uppercase tracking-widest">{selectedSubmission.UserInternship?.Internship?.title}</p>
                  </div>
                </div>

                <div className="glass-card p-6 border-white/5 space-y-4">
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Project Repository</p>
                  <a 
                    href={selectedSubmission.project_link} 
                    target="_blank" 
                    rel="noreferrer"
                    className="flex items-center gap-2 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-indigo-500/50 hover:bg-indigo-500/10 text-indigo-400 transition-all font-bold"
                  >
                    <Rocket size={18} />
                    <span className="truncate">{selectedSubmission.project_link}</span>
                  </a>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="glass-card p-5">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Difficulty</p>
                    <p className="text-sm font-bold text-white uppercase">{selectedSubmission.difficulty_rating || 'MEDIUM'}</p>
                  </div>
                  <div className="glass-card p-5">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Status</p>
                    <p className="text-sm font-bold text-white uppercase">{selectedSubmission.UserInternship?.status}</p>
                  </div>
                </div>

                <div className="space-y-4">
                   <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Evaluation Decision</p>
                   <div className="flex gap-4">
                     <button 
                       onClick={() => handleEvaluate(selectedSubmission.UserInternship?.id, 'accepted', 'Your project met all operational parameters. Excellence confirmed.')}
                       className="flex-1 btn-premium btn-premium-primary py-4 shadow-emerald-500/20 bg-emerald-500"
                     >
                       <CheckCircle size={18} /> Confirm Pass
                     </button>
                     <button 
                       onClick={() => handleEvaluate(selectedSubmission.UserInternship?.id, 'rejected', 'The project failed to meet the specified core requirements. Please re-align and re-submit.')}
                       className="flex-1 btn-premium btn-premium-danger py-4"
                     >
                       <XCircle size={18} /> Require Re-alignment
                     </button>
                   </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminInternshipsPage;
